/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import { ButtonMain, Card, Input } from '@quero-delivery/quero-components-web';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { toast } from 'react-toastify';
import { useMatch, useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import {
  celularLabel,
  cepLabel,
  cnpjLabel,
  cpfLabel,
  objectNotNull,
  removeNoNumeric,
  validateCpf,
  validateEmail,
  validatePhoneBr,
} from '@/Utils/FnUtils';
import Requests from '@/Services/Requests';
import MapWithPin from '@/Components/map-with-pin/MapWithPin';

import styles from './PlaceForm.module.scss';
import { requiredErrorMessage } from '@/infra/YupDecorator';
import { selectPlaceSelectedToEdit } from '@/_store/modules/places';
import type { Place } from '@/_types/places';
import { useAppSelector } from '@/_store/hooks';
import { CITIES_ID_WITH_360_POWER } from '../constants';
import { isProd } from '@/_config/env';
import { usePostSendEvent } from '@/api/post-send-event';
import { EVENTS_KEYS } from '@/_constants/events';
import { useCities } from '@/_hooks/use-cities';
import type { PlaceFormFields } from '@/_features/places/types';
import { getDocumentType, normalizeCnpj, validateCnpj } from '@/_utils/text';
import { GET_PLACE_ID } from '@/_features/places/api/constants';

interface IForm {
  nomeExibicao: string;
  municipioId: string;
  celular: string;
  logradouro: string;
  numero: string;
  bairro: string;
  cep: string;
  referencia: string;
  complemento?: string;
  latitude: string;
  longitude: string;
  cpfCnpj: string;
  email: string;
  senha: string;
  idExternal360Power?: string;
}

interface IPosition {
  latitude: number;
  longitude: number;
}

type IActionForm = 'cadastrar' | 'editar';

function PlaceForm() {
  const navigate = useNavigate();
  const match = useMatch({ path: '/estabelecimentos/:placeId/editar' });

  const placeSelectedToEdit = useAppSelector(selectPlaceSelectedToEdit);

  const [actionForm, setActionForm] = useState<IActionForm>('cadastrar');
  const [loadingSubmitForm, setLoadingSubmitForm] = useState(false);
  const [wasLatLongEmpty, setWasLatLongEmpty] = useState(false);
  const [locationBeforeEdit, setLocationBeforeEdit] = useState<IPosition>();

  const [positionCity, setPositionCity] = useState<IPosition>({
    latitude: -10.2317393,
    longitude: -50.0854409,
  });

  const queryClient = useQueryClient();

  const [zoom, setZoom] = useState(5);
  const [loadingMap, setLoadingMap] = useState(false);

  const selectedCityOnDashboard = useAppSelector(
    state => state.filters.cityIdOnDashboard,
  );

  const { cities } = useCities();

  const selectedCityLocation = cities?.find(
    city => city._id === selectedCityOnDashboard,
  )?.localizacao;

  const { mutate: sendEvent } = usePostSendEvent();

  const {
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<IForm>();

  const latitudePlace = watch('latitude');
  const longitudePlace = watch('longitude');

  useEffect(() => {
    if (placeSelectedToEdit) {
      adjustValuesToEdit(placeSelectedToEdit as any);
    } else if (match?.pathname.includes('/editar')) {
      navigate('/estabelecimentos');
    }
  }, [placeSelectedToEdit]);

  useEffect(() => {
    if (selectedCityLocation) {
      setLoadingMap(true);
      const newPosition = {
        latitude: selectedCityLocation.latitude,
        longitude: selectedCityLocation.longitude,
      };
      if (!placeSelectedToEdit) {
        setPositionCity(newPosition);

        setValue('latitude', String(newPosition.latitude));
        setValue('longitude', String(newPosition.longitude));
      }
      setZoom(14.5);

      setTimeout(() => setLoadingMap(false), 1000);
    }
  }, [selectedCityLocation]);

  const submitFormNewPlace: SubmitHandler<IForm> = async data => {
    const cpfFormat = removeNoNumeric(data.cpfCnpj);

    const isCnpj = getDocumentType(data.cpfCnpj) === 'cnpj';

    const payload: PlaceFormFields = {
      municipioId: placeSelectedToEdit!.municipioId,
      nomeExibicao: data.nomeExibicao,
      celular: removeNoNumeric(data.celular),
      endereco: {
        logradouro: data.logradouro,
        numero: data.numero,
        bairro: data.bairro,
        municipioId: placeSelectedToEdit!.municipioId,
        cep: data.cep,
        referencia: data.referencia,
        complemento: data.complemento,
      },
      localizacao: {
        latitude: `${Number(data.latitude).toPrecision(9)}`,
        longitude: `${Number(data.longitude).toPrecision(9)}`,
      },
      cnpj: isCnpj ? normalizeCnpj(data.cpfCnpj) : '',
      cpf: isCnpj ? '' : cpfFormat,
      email: data.email,
      senha: data.senha,
      idExternal360Power: data.idExternal360Power,
    };

    try {
      switch (actionForm) {
        case 'editar':
          if (placeSelectedToEdit)
            await editPlace(placeSelectedToEdit._id, payload);
          break;
        default:
          break;
      }
    } catch (err: any) {
      if (err instanceof Error) {
        toast.error(err.message);
      }
    } finally {
      setLoadingSubmitForm(false);
    }
  };

  async function editPlace(placeId: string, payload: PlaceFormFields) {
    setLoadingSubmitForm(true);

    try {
      const response: any = await Requests.places.editPlace(
        placeId,
        objectNotNull(payload),
      );

      if (response.r) {
        await queryClient.invalidateQueries({
          queryKey: [GET_PLACE_ID],
        });

        toast.success('Estabelecimento editado com sucesso');
        navigate('/estabelecimentos');
      } else {
        toast.warn(response.errors.join(' - '));
      }
    } catch (error: any) {
      toast.warn(error.response?.data.error);
    } finally {
      if (wasLatLongEmpty) {
        sendEvent({
          eventName: EVENTS_KEYS.ESTABELECIMENTO_RETORNAR_LOCALIZAÇÃO_DEFAULT,
          payload: {
            place: {
              placeId: placeSelectedToEdit?._id,
              placeName: placeSelectedToEdit?.nomeExibicao,
              city: placeSelectedToEdit?.cityName,
            },
            newLocation: {
              latitude: Number(latitudePlace),
              longitude: Number(longitudePlace),
            },
            oldLocation: locationBeforeEdit,
          },
        });
      }
    }
  }

  function adjustValuesToEdit(place: Place) {
    setLoadingMap(true);

    setActionForm('editar');

    setValue('nomeExibicao', place.nomeExibicao);
    setValue(
      'cpfCnpj',
      place.cpf ? cpfLabel(place.cpf) : cnpjLabel(place.cnpj),
    );
    setValue('celular', celularLabel(place.celular));
    setValue('email', place.email);
    setValue('logradouro', place.endereco.logradouro);
    setValue('referencia', place.endereco.referencia);
    setValue('numero', place.endereco.numero);
    setValue('complemento', place.endereco.complemento);
    setValue('bairro', place.endereco.bairro);
    setValue('cep', cepLabel(place.endereco.cep));
    setValue('idExternal360Power', place.idExternal360Power || '');

    const hasLatLong =
      !!place.localizacao &&
      !!place.localizacao.latitude &&
      !!place.localizacao.longitude;

    setWasLatLongEmpty(!hasLatLong);
    setLocationBeforeEdit({
      latitude: Number(latitudePlace),
      longitude: Number(longitudePlace),
    });

    if (hasLatLong) {
      setValue('latitude', String(place.localizacao.latitude));
      setValue('longitude', String(place.localizacao.longitude));

      setPositionCity({
        latitude: place.localizacao.latitude,
        longitude: place.localizacao.longitude,
      });
    }

    setTimeout(() => setLoadingMap(false), 1000);
  }

  const shouldShow360PowerField = () => {
    if (!isProd) {
      return true;
    }

    if (placeSelectedToEdit?.municipioId) {
      return CITIES_ID_WITH_360_POWER.includes(
        placeSelectedToEdit?.municipioId,
      );
    }

    return false;
  };

  const canShow360PowerField = shouldShow360PowerField();

  const formatDocument = (value: string) => {
    return getDocumentType(value) === 'cpf'
      ? cpfLabel(value)
      : cnpjLabel(value);
  };

  const validateDocument = (value: string) => {
    const document = removeNoNumeric(value);

    return getDocumentType(value) === 'cpf'
      ? validateCpf(document)
      : validateCnpj(value);
  };

  return (
    <div className="container">
      <div className={styles.containerRegister}>
        <div className="register-subtitle">
          <div className="subtitle-icon" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faChevronLeft} size="lg" />
          </div>
          <strong>Editar estabelecimento</strong>
        </div>

        <form
          onSubmit={handleSubmit(submitFormNewPlace)}
          className={styles.formNewPlace}
        >
          <section className={styles.sectionForm}>
            <div className={styles.sectionTitle}>
              <span>Informações Pessoais</span>
            </div>
            <div className={styles.sectionFields}>
              <div className={styles.sectionLine}>
                <div
                  className={
                    canShow360PowerField ? styles.lineRight : styles.lineUnique
                  }
                >
                  <Controller
                    name="nomeExibicao"
                    control={control}
                    rules={{ required: requiredErrorMessage }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Nome do estabelecimento"
                        errorMessage={errors.nomeExibicao?.message}
                      />
                    )}
                  />
                </div>
                {canShow360PowerField && (
                  <div className={styles.lineLeft}>
                    <Controller
                      name="idExternal360Power"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="id externo 360 Power"
                          errorMessage={errors.idExternal360Power?.message}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
              <div className={styles.sectionLine}>
                <div className={styles.lineRight}>
                  <Controller
                    name="cpfCnpj"
                    control={control}
                    rules={{
                      required: requiredErrorMessage,
                      validate: validateDocument,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="CPF/CNPJ"
                        onChange={event => {
                          const { value } = event.target;
                          return field.onChange(formatDocument(value));
                        }}
                        maxLength={18}
                        errorMessage={errors.cpfCnpj?.message}
                      />
                    )}
                  />
                </div>
                <div className={styles.lineLeft}>
                  <Controller
                    name="celular"
                    control={control}
                    rules={{
                      required: requiredErrorMessage,
                      validate: validatePhoneBr,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Celular"
                        errorMessage={errors.celular?.message}
                        onChange={event => {
                          const { value } = event.target;
                          return field.onChange(celularLabel(value));
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              <div className={styles.sectionLine}>
                <div className={styles.lineRight}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: requiredErrorMessage,
                      validate: validateEmail,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Email"
                        errorMessage={errors.email?.message}
                      />
                    )}
                  />
                </div>
                <div className={styles.lineLeft}>
                  <Controller
                    name="senha"
                    control={control}
                    rules={{
                      required:
                        actionForm != 'editar'
                          ? requiredErrorMessage
                          : undefined,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Senha"
                        errorMessage={errors.senha?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </section>
          <section className={styles.sectionForm}>
            <div className={styles.sectionTitle}>Endereço</div>
            <div className={styles.sectionFields}>
              <div className={styles.sectionMap}>
                <Card
                  loading={loadingMap}
                  style={{ height: '100%' }}
                  type="shadow"
                >
                  <MapWithPin
                    loadingMap={loadingMap}
                    onChangePin={position => {
                      setValue('latitude', position.lat.toPrecision(9));
                      setValue('longitude', position.lng.toPrecision(9));
                    }}
                    popupLabel="Local do estabelecimento"
                    mainPosition={positionCity}
                    objectPosition={{
                      latitude: Number(latitudePlace),
                      longitude: Number(longitudePlace),
                    }}
                    zoom={zoom}
                  />
                </Card>
              </div>
              <div className={styles.sectionLine}>
                <div className={styles.lineRight}>
                  <Controller
                    name="latitude"
                    control={control}
                    defaultValue={String(positionCity.latitude)}
                    rules={{
                      required: requiredErrorMessage,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Latitude"
                        errorMessage={errors.latitude?.message}
                      />
                    )}
                  />
                </div>
                <div className={styles.lineLeft}>
                  <Controller
                    name="longitude"
                    control={control}
                    defaultValue={String(positionCity.longitude)}
                    rules={{
                      required: requiredErrorMessage,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Longitude"
                        errorMessage={errors.longitude?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className={styles.sectionLine}>
                <div className={styles.lineRight}>
                  <Controller
                    name="logradouro"
                    control={control}
                    rules={{
                      required: requiredErrorMessage,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Logradouro"
                        errorMessage={errors.logradouro?.message}
                      />
                    )}
                  />
                </div>
                <div className={styles.lineLeft}>
                  <Controller
                    name="referencia"
                    control={control}
                    rules={{
                      required: requiredErrorMessage,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Referência"
                        errorMessage={errors.referencia?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className={styles.sectionLine}>
                <div className={styles.lineRight}>
                  <Controller
                    name="numero"
                    control={control}
                    rules={{
                      required: requiredErrorMessage,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Número"
                        errorMessage={errors.numero?.message}
                      />
                    )}
                  />
                </div>
                <div className={styles.lineLeft}>
                  <Controller
                    name="complemento"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Complemento"
                        errorMessage={errors.complemento?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className={styles.sectionLine}>
                <div className={styles.lineRight}>
                  <Controller
                    name="bairro"
                    control={control}
                    rules={{
                      required: requiredErrorMessage,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Bairro"
                        errorMessage={errors.bairro?.message}
                      />
                    )}
                  />
                </div>
                <div className={styles.lineLeft}>
                  <Controller
                    name="cep"
                    control={control}
                    rules={{
                      required: requiredErrorMessage,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="CEP"
                        errorMessage={errors.cep?.message}
                        onChange={event => {
                          const { value } = event.target;
                          return field.onChange(cepLabel(value));
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </section>
          <div className={styles.formButtonSubmit}>
            <ButtonMain
              textFirst="cancelar"
              firstStrong
              type="button"
              width="100%"
              onClick={() => navigate(-1)}
              noBorder
              disabled={loadingSubmitForm}
            />
            <ButtonMain
              textFirst={actionForm}
              firstStrong
              width="100%"
              type="submit"
              loading={loadingSubmitForm}
              disabled={loadingSubmitForm}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

export default PlaceForm;
